import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Trash2, Plus, Play, RefreshCw } from 'lucide-react'
import { api } from '../services/api'
import { CourseDetail, Material } from '../types'
import LoadingSpinner from '../components/LoadingSpinner'
import { useWebSocket } from '../hooks/useWebSocket'

function daysUntil(iso?: string): number | null {
  if (!iso) return null
  const target = new Date(iso).getTime()
  const now = Date.now()
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24))
}

export default function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseDetail | null>(null)
  const [allMaterials, setAllMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [generatingQuiz, setGeneratingQuiz] = useState(false)
  const [quizDifficulty, setQuizDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium')
  const [quizCount, setQuizCount] = useState(15)

  const load = async () => {
    if (!courseId) return
    setLoading(true)
    try {
      const [c, m] = await Promise.all([
        api.getCourse(courseId),
        api.getMaterials() as Promise<any>,
      ])
      setCourse(c)
      setAllMaterials(m.materials ?? m ?? [])
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar el curso')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() /* eslint-disable-next-line */ }, [courseId])

  useWebSocket({
    onMessage: (msg) => {
      if (!course) return
      if (msg.type === 'course_quiz_ready' && msg.course_id === course.courseId) {
        load()
      }
      if (msg.type === 'course_quiz_failed' && msg.course_id === course.courseId) {
        setError(msg.error || 'Falló la generación del quiz')
        load()
      }
    },
    onConnect: () => {},
    onDisconnect: () => {},
  })

  const handleGenerateQuiz = async () => {
    if (!course) return
    setGeneratingQuiz(true); setError(null)
    try {
      await api.generateCourseQuiz(course.courseId, {
        question_count: quizCount,
        difficulty: quizDifficulty,
      })
      await load()
    } catch (e: any) {
      setError(e.message || 'No se pudo iniciar la generación del quiz')
    } finally {
      setGeneratingQuiz(false)
    }
  }

  const handleRemoveMaterial = async (mid: string) => {
    if (!course) return
    if (!confirm('¿Quitar este material del curso?')) return
    try {
      await api.removeMaterialFromCourse(course.courseId, mid)
      await load()
    } catch (e: any) {
      setError(e.message || 'No se pudo quitar el material')
    }
  }

  const handleAdd = async (mid: string) => {
    if (!course) return
    try {
      await api.addMaterialToCourse(course.courseId, mid)
      setShowAdd(false)
      await load()
    } catch (e: any) {
      setError(e.message || 'No se pudo agregar el material')
    }
  }

  const handleDeleteCourse = async () => {
    if (!course) return
    if (!confirm(`¿Eliminar el curso "${course.name}"? Los materiales NO se borran.`)) return
    try {
      await api.deleteCourse(course.courseId)
      navigate('/courses')
    } catch (e: any) {
      setError(e.message || 'No se pudo eliminar el curso')
    }
  }

  if (loading) return <div className="flex items-center justify-center min-h-[50vh]"><LoadingSpinner /></div>
  if (!course) return <div className="text-center p-12" style={{ color: '#94A3B8' }}>Curso no encontrado</div>

  const daysLeft = daysUntil(course.examDate)
  const canGenerateQuiz = (course.materialIds || []).length >= 2
  const materialIdsInCourse = new Set(course.materialIds || [])
  const addable = allMaterials.filter((m) => !materialIdsInCourse.has(m.materialId))
  const quizStatus = course.course_quiz_status

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <Link to="/courses" className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}>
        <ArrowLeft className="w-4 h-4" /> Volver a cursos
      </Link>

      <div
        className="glass-card p-6"
        style={{ borderLeft: `4px solid ${course.color || '#8b5cf6'}` }}
      >
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>{course.name}</h1>
            {course.description && (
              <p className="text-sm mt-2" style={{ color: '#94A3B8' }}>{course.description}</p>
            )}
            {daysLeft !== null && (
              <div
                className="inline-flex items-center gap-2 mt-3 px-3 py-1 rounded-full text-xs"
                style={{
                  background: daysLeft <= 7 ? 'rgba(244,63,94,0.1)' : 'rgba(255,255,255,0.05)',
                  color: daysLeft <= 7 ? '#FB7185' : '#94A3B8',
                }}
              >
                <Calendar className="w-3 h-3" />
                {daysLeft > 0 ? `${daysLeft} día${daysLeft === 1 ? '' : 's'} al examen` :
                 daysLeft === 0 ? 'Examen hoy' : 'Examen pasado'}
              </div>
            )}
          </div>
          <button
            onClick={handleDeleteCourse}
            className="p-2 rounded-lg hover:bg-white/5"
            title="Eliminar curso"
          >
            <Trash2 className="w-4 h-4" style={{ color: '#FB7185' }} />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="p-4 rounded-xl text-sm"
          style={{ background: 'rgba(244,63,94,0.08)', color: '#FB7185', border: '1px solid rgba(244,63,94,0.2)' }}
        >
          {error}
        </div>
      )}

      {/* Materials */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold" style={{ color: '#F1F5F9' }}>
            Materiales ({course.materials.length})
          </h2>
          <button
            onClick={() => setShowAdd((s) => !s)}
            className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(79,70,229,0.15)', color: '#818CF8' }}
          >
            <Plus className="w-3.5 h-3.5" /> Agregar
          </button>
        </div>

        {showAdd && (
          <div
            className="mb-4 rounded-lg p-3"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {addable.length === 0 ? (
              <p className="text-xs" style={{ color: '#64748B' }}>
                No hay más materiales para agregar.
              </p>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {addable.map((m) => (
                  <button
                    key={m.materialId}
                    onClick={() => handleAdd(m.materialId)}
                    className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-white/5 truncate"
                    style={{ color: '#E2E8F0' }}
                  >
                    {m.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {course.materials.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: '#64748B' }}>
            Agrega materiales para armar el curso.
          </p>
        ) : (
          <div className="space-y-2">
            {course.materials.map((m) => (
              <div
                key={m.materialId}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.03)' }}
              >
                <Link
                  to={`/study/${m.materialId}`}
                  className="flex-1 text-sm truncate"
                  style={{ color: '#E2E8F0' }}
                >
                  {m.title}
                </Link>
                <button
                  onClick={() => handleRemoveMaterial(m.materialId)}
                  className="p-1 rounded hover:bg-white/5"
                  title="Quitar del curso"
                >
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#64748B' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cross-material Quiz */}
      <div className="glass-card p-5">
        <h2 className="font-semibold mb-1" style={{ color: '#F1F5F9' }}>Quiz del curso</h2>
        <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
          Combina preguntas de todos los materiales, mezcladas para practicar como en un examen real.
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <div>
            <label className="block text-xs mb-1" style={{ color: '#64748B' }}>Preguntas</label>
            <select
              value={quizCount}
              onChange={(e) => setQuizCount(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {[10, 15, 20, 25, 30].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: '#64748B' }}>Dificultad</label>
            <select
              value={quizDifficulty}
              onChange={(e) => setQuizDifficulty(e.target.value as any)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#F1F5F9', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <option value="easy">Fácil</option>
              <option value="medium">Medio</option>
              <option value="hard">Difícil</option>
            </select>
          </div>
        </div>

        {!canGenerateQuiz && (
          <p className="text-xs mb-3" style={{ color: '#F59E0B' }}>
            Necesitas al menos 2 materiales para generar un quiz combinado.
          </p>
        )}

        <button
          onClick={handleGenerateQuiz}
          disabled={!canGenerateQuiz || generatingQuiz || quizStatus === 'processing'}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm"
          style={{
            background: '#4F46E5',
            color: '#fff',
            opacity: (!canGenerateQuiz || generatingQuiz || quizStatus === 'processing') ? 0.5 : 1,
          }}
        >
          {quizStatus === 'processing' || generatingQuiz ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Generando…</>
          ) : quizStatus === 'ready' ? (
            <><RefreshCw className="w-4 h-4" /> Regenerar quiz</>
          ) : (
            <><Play className="w-4 h-4" /> Generar quiz del curso</>
          )}
        </button>

        {quizStatus === 'ready' && course.course_quiz_latest_version && (
          <p className="text-xs mt-3" style={{ color: '#34D399' }}>
            Última versión lista — {course.course_quiz_question_count} preguntas · {course.course_quiz_difficulty}
          </p>
        )}
      </div>
    </div>
  )
}
